import React from 'react';
import { useLanguage } from './LanguageContext';
import { translations } from '../data/translations';
import { Car, UserCheck, MapPin, CarFront, Building2, Home } from 'lucide-react';
import { ArabicPattern } from './ArabicPattern';

export const Services = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const services = [
    {
      icon: Car,
      title: t.services.carRental.title,
      description: t.services.carRental.description,
      gradient: 'from-teal-500 to-cyan-600'
    },
    {
      icon: UserCheck,
      title: t.services.chauffeur.title,
      description: t.services.chauffeur.description,
      gradient: 'from-gray-400 to-teal-500'
    },
    {
      icon: MapPin,
      title: t.services.tours.title,
      description: t.services.tours.description,
      gradient: 'from-teal-500 to-emerald-600'
    },
    {
      icon: CarFront,
      title: t.services.usedCars.title,
      description: t.services.usedCars.description,
      gradient: 'from-slate-400 to-cyan-600'
    },
    {
      icon: Building2,
      title: t.services.construction.title,
      description: t.services.construction.description,
      gradient: 'from-teal-600 to-gray-500'
    },
    {
      icon: Home,
      title: t.services.realEstate.title,
      description: t.services.realEstate.description,
      gradient: 'from-gray-500 to-teal-500'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Arabic Pattern Background */}
      <ArabicPattern className="opacity-30" />
      
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t.services.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-teal-500/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/20"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className={`text-2xl font-bold text-white mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {service.title}
                </h3>
                <p className={`text-gray-400 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                  {service.description}
                </p>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-500/0 to-teal-600/0 group-hover:from-teal-500/10 group-hover:to-teal-600/10 transition-all duration-500 pointer-events-none"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};